import styled, { css } from 'styled-components';
import { animated } from 'react-spring';

import {
  buttonReset,
  listReset,
  heading_xl,
  heading_l,
  heading_m,
  heading_s,
  heading_xs,
  text_l,
  safeFocusRemoval,
  focusOutline,
  color,
  text_s,
  mq,
  showOnPrint,
  hideOnPrint
} from '_utils/styles';
import { cssInlineSvg } from '_utils/styles/svg';
import { spacing } from '_utils/props';

import { RichTextStyle } from '_components/Corporate/ContentRichText/StyledContentRichText';

export const SectionTitle = styled.h2`
  margin: 0;

  h2& {
    ${heading_xl}
  }

  h3& {
    ${heading_l}
  }

  h4& {
    ${heading_m}
  }

  h5& {
    ${heading_s}
  }

  h6& {
    ${heading_xs}
  }
`;

export const SectionDescription = styled.div`
  ${RichTextStyle};
  ${text_l}
  margin: 0;
  ${({ theme }) => spacing({ theme, $mt: 3 })}
`;

export const Nav = styled.nav`
  position: relative;
`;

export const ListWrapper = styled.div`
  padding: 6px; // for focus state
  position: relative;
  overflow: hidden;
  height: 64px;
  ${hideOnPrint}
`;

export const Tablist = styled(animated.ul)`
  ${listReset}
  display: inline-block;
  position: absolute;
  height: 100%;
  left: 4px;
  top: 4px;
  white-space: nowrap;
  min-width: 100%;

  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

export const TabItem = styled.li`
  display: inline-block;
  height: 100%;

  & + & {
    ${({ theme }) => spacing({ theme, $ml: 3 })}
  }
`;

export const TabButton = styled.button<{ $active?: boolean }>`
  ${buttonReset()}
  ${heading_s}
  ${({ theme }) => spacing({ theme, $py: 2 })}
  position: relative;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.all.green};
    transform: scaleY(0);
    transform-origin: center bottom;
    opacity: 0;
    transition: 400ms opacity, 400ms transform;
  }

  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
  }


  ${({ $active }) => {
    if ($active) {
      return css`
        &:after {
          transform: scaleY(1);
          opacity: 1;
        }
      `;
    }

    return css`
      font-weight: normal;

      &:hover {
        color: green;
      }
    `;
  }}

  &[disabled] {
    pointer-events: none;
  }
`;

const NavButton = styled.button<{ $active?: boolean }>`
  ${buttonReset()}

  ${({ $active }) => {
    if (!$active) {
      return css`
        opacity: 0;
        pointer-events: none;
      `;
    }

    return null;
  }}

  width: 32px;
  height: 32px;
  background-color: #f0f1f2;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  transition: opacity 200ms 0ms ease;

  &:before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-position: center;
  }

  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
  }
`;

export const LeftNavButton = styled(NavButton)`
  left: 0;

  &:before {
    background-image: ${cssInlineSvg(
      `<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.07074 5.40962L6.07023 0.413154C6.14769 0.335102 6.23985 0.273149 6.34139 0.230872C6.44293 0.188595 6.55184 0.166829 6.66184 0.166829C6.77184 0.166829 6.88075 0.188595 6.98229 0.230872C7.08383 0.273149 7.17598 0.335102 7.25344 0.413154C7.33154 0.490569 7.39353 0.58267 7.43584 0.684148C7.47814 0.785625 7.49992 0.89447 7.49992 1.0044C7.49992 1.11433 7.47814 1.22318 7.43584 1.32466C7.39353 1.42613 7.33154 1.51824 7.25344 1.59565C5.75315 3.01073 4.2883 4.46404 2.91637 6.00428L7.25344 10.4061C7.41035 10.5629 7.4985 10.7756 7.4985 10.9973C7.4985 11.2191 7.41035 11.4318 7.25344 11.5886C7.09654 11.7454 6.88373 11.8335 6.66184 11.8335C6.55197 11.8335 6.44317 11.8119 6.34166 11.7698C6.24015 11.7278 6.14792 11.6662 6.07023 11.5886L1.07074 6.59212C0.99488 6.51292 0.935416 6.41954 0.895758 6.31732C0.812418 6.11457 0.812418 5.88717 0.895758 5.68443C0.935416 5.58221 0.99488 5.48882 1.07074 5.40962Z" fill="#188838" /></svg>`
    )};
  }
`;
export const RightNavButton = styled(NavButton)`
  right: 0;

  &:before {
    background-image: ${cssInlineSvg(
      `<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.92926 6.59038L1.92977 11.5868C1.85231 11.6649 1.76015 11.7269 1.65861 11.7691C1.55707 11.8114 1.44816 11.8332 1.33816 11.8332C1.22816 11.8332 1.11925 11.8114 1.01771 11.7691C0.916175 11.7269 0.824017 11.6649 0.746556 11.5868C0.668457 11.5094 0.606469 11.4173 0.564166 11.3159C0.521863 11.2144 0.500082 11.1055 0.500082 10.9956C0.500082 10.8857 0.521863 10.7768 0.564166 10.6753C0.606469 10.5739 0.668458 10.4818 0.746556 10.4044C2.24685 8.98927 3.7117 7.53596 5.08363 5.99572L0.746557 1.59391C0.589653 1.4371 0.501505 1.22442 0.501505 1.00266C0.501505 0.780895 0.589653 0.568216 0.746557 0.411407C0.90346 0.254598 1.11627 0.166504 1.33816 0.166504C1.44803 0.166504 1.55683 0.188132 1.65834 0.230152C1.75985 0.272173 1.85208 0.333763 1.92977 0.411407L6.92926 5.40788C7.00512 5.48708 7.06458 5.58046 7.10424 5.68268C7.18758 5.88543 7.18758 6.11283 7.10424 6.31557C7.06458 6.41779 7.00512 6.51118 6.92926 6.59038Z" fill="#188838" /></svg>`
    )};
  }
`;

export const ContentTab = styled.div`
  @media print {
    margin-top: 24px;
  }
  &.is-hidden {
    display: none;
    ${showOnPrint}
  }
`;

export const ContentTabPrintTitle = styled.p`
  display: none;

  ${heading_l}

  ${showOnPrint}
`;

export const FeeWrapper = styled.div`
  ${({ theme }) => spacing({ theme, $py: 3 })}
  display: flex;
  flex-direction: column;

  &:last-of-type {
    padding-bottom: 0;
  }

  ${mq('sm')} {
    flex-direction: row;
  }

  & + & {
    border-top: 1px dashed ${({ theme }) => theme.colors.all.deepBlueTint15};
  }
`;

export const FeeTitle = styled.h3`
  ${heading_m}
  margin: 0;
`;

export const FeeDescription = styled.p`
  ${text_s};
  color: ${({ theme }) => theme.colors.all.deepBlueTint75};
  margin: 0;
  ${({ theme }) => spacing({ theme, $mt: 1 })}
`;

export const TextWrapper = styled.div`
  flex-grow: 1;
`;

export const CostWrapper = styled.div`
  ${mq('sm')} {
    ${({ theme }) => spacing({ theme,$ml: 5 })}
    max-width: 200px;
    text-align: right;
  }
`;

export const Cost = styled.h4`
  ${heading_m}
  margin: 0;
  ${({ theme }) => spacing({ theme, $mt: 2 })}

  ${mq('sm')} {
    margin: 0;
  }
`;

export const CostModifier = styled.p`
  ${text_s};
  color: ${({ theme }) => theme.colors.all.deepBlueTint75};
  margin: 0;
  ${({ theme }) => spacing({ theme, $mt: 1 })}
`;

export const EditorDiv = styled.div``;

export const Section = styled.article``;
